import { CountMetric, LatencyMetric, EnvironmentDetail, UnsupportedEnvironmentException, PromiseUtils } from '@amzn/ihm-associate-tools-js-lib'
import * as CandyCaneService from '@services/CandyCaneService'
import { CandyCaneBluetoothErrorMappings } from '@services/bluetooth/CandyCaneBluetoothErrorMappings'

const BluetoothSerialCommTimeout = Object.freeze({
    DEFAULT: 30000,
})

const programName = 'IhmFlyingDutchman'

export class BluetoothSerialCommService {
    // Public functions

    /**
     * sends data to the connected device.
     *
     * [
     *   {
     *     macAddress: "1A:1A:1A:1A:1A:1A",
     *     data: "^A",
     *   }
     * ]
     *
     * @return {Promise}     - success when the BT Device has received the job.
     *                       - exception when plugin returns error
     */
    public static writeDataSerialComm (args) {
        if (!EnvironmentDetail.isAndroid()) {
            console.error('Bluetooth is only supported on Android')
            return Promise.reject(new UnsupportedEnvironmentException({
                message: 'Bluetooth writeDataSerialComm is only supported on Android.',
            }))
        }

        const writeDataSerialCommFaultMetrics = new CountMetric(undefined, 'Fault', programName, 'bluetoothCoreService.writeDataSerialComm')
        const writeDataSerialCommLatencyMetrics = new LatencyMetric(undefined, 'Time', programName, 'bluetoothCoreService.writeDataSerialComm')
        writeDataSerialCommLatencyMetrics.start()
        console.info('ATBase BluetoothSerialCommService writeDataSerialComm CandyCaneService.bluetoothWriteDataSerialComm: ' + JSON.stringify(args))
        return CandyCaneService.bluetoothWriteDataSerialComm(args)
            .then(() => {
                console.info('BluetoothSerialComm data sent successfully')
                writeDataSerialCommFaultMetrics.count(0)
            })
            .catch((error) => {
                console.error('BluetoothSerialComm writeDataSerialComm() error: ', error)
                writeDataSerialCommFaultMetrics.count(1)
                return Promise.reject(CandyCaneBluetoothErrorMappings.getExceptionForError(error))
            })
            .finally(() => {
                writeDataSerialCommLatencyMetrics.stop()
            })
    }

    /**
     * Changes standard charset
     *
     * [
     *   {
     *     charsetName: "^A",
     *   }
     * ]
     *
     * @return {Promise}     - success when the BT Device has received the job.
     *                       - exception when plugin returns error
     */
    public static changeCharsetSerialComm (args) {
        if (!EnvironmentDetail.isAndroid()) {
            console.error('Bluetooth is only supported on Android')
            return Promise.reject(new UnsupportedEnvironmentException({
                message: 'Bluetooth changeCharsetSerialComm is only supported on Android.',
            }))
        }

        const changeCharsetSerialCommFaultMetrics = new CountMetric(undefined, 'Fault', programName, 'bluetoothCoreService.changeCharsetSerialComm')
        const changeCharsetSerialCommLatencyMetrics = new LatencyMetric(undefined, 'Time', programName, 'bluetoothCoreService.changeCharsetSerialComm')
        changeCharsetSerialCommLatencyMetrics.start()
        console.info('ATBase BluetoothSerialCommService changeCharsetSerialComm CandyCaneService.bluetoothChangeCharsetSerialComm: ' + JSON.stringify(args))
        return CandyCaneService.bluetoothChangeCharsetSerialComm(args)
            .then(() => {
                console.info('BluetoothSerialComm change standard charset successful')
                changeCharsetSerialCommFaultMetrics.count(0)
            })
            .catch((error) => {
                console.error('BluetoothSerialComm changeCharsetSerialComm() error: ', error)
                changeCharsetSerialCommFaultMetrics.count(1)
                return Promise.reject(CandyCaneBluetoothErrorMappings.getExceptionForError(error))
            })
            .finally(() => {
                changeCharsetSerialCommLatencyMetrics.stop()
            })
    }

    /**
     * Closes bluetooth socket connection
     *
     * @return {Promise}     - success when the BT Device has received the job.
     *                       - exception when plugin returns error
     */
    public static disconnectSerialComm (args) {
        if (!EnvironmentDetail.isAndroid()) {
            console.error('Bluetooth is only supported on Android')
            return Promise.reject(new UnsupportedEnvironmentException({
                message: 'Bluetooth disconnect is only supported on Android.',
            }))
        }

        const disconnectSerialCommFaultMetrics = new CountMetric(undefined, 'Fault', programName, 'bluetoothCoreService.disconnectSerialComm')
        const disconnectSerialCommLatencyMetrics = new LatencyMetric(undefined, 'Time', programName, 'bluetoothCoreService.disconnectSerialComm')
        disconnectSerialCommLatencyMetrics.start()
        console.info('ATBase BluetoothSerialCommService disconnectSerialComm CandyCaneService.bluetoothDisconnectSerialComm: ' + JSON.stringify(args))
        return CandyCaneService.bluetoothDisconnectSerialComm(args)
            .then(() => {
                console.info('BluetoothSerialComm bluetooth socket connection closed successfully')
                disconnectSerialCommFaultMetrics.count(0)
            })
            .catch((error) => {
                console.error('BluetoothSerialComm disconnectSerialComm() error: ', error)
                disconnectSerialCommFaultMetrics.count(1)
                return Promise.reject(CandyCaneBluetoothErrorMappings.getExceptionForError(error))
            })
            .finally(() => {
                disconnectSerialCommLatencyMetrics.stop()
            })
    }

    /**
     * Sends a command to BT device with a given mac address and returns the response received from BT device.
     *
     * [
     *   {
     *     macAddress: "1A:1A:1A:1A:1A:1A",
     *     command: "^A",
     *     responseSizeInBytes: "100",
     *     timeoutInMS: "1000"
     *   }
     * ]
     *
     * @return {Promise}    - success when the dara is successfully retrieved.
     *                      - exception when plugin returns error
     */
    public static requestDataSerialComm (args) {
        if (!EnvironmentDetail.isAndroid()) {
            console.error('Bluetooth is only supported on Android')
            return Promise.reject(new UnsupportedEnvironmentException({
                message: 'Bluetooth is only supported on Android.',
            }))
        }

        const requestDataSerialCommFaultMetrics = new CountMetric(undefined, 'Fault', programName, 'bluetoothCoreService.requestDataSerialComm')
        const requestDataSerialCommLatencyMetrics = new LatencyMetric(undefined, 'Time', programName, 'bluetoothCoreService.requestDataSerialComm')
        requestDataSerialCommLatencyMetrics.start()
        console.info('ATBase BluetoothSerialCommService requestDataSerialComm CandyCaneService.bluetoothRequestDataSerialComm: ' + JSON.stringify(args))
        const bluetoothRequestDataSerialCommPromise = CandyCaneService.bluetoothRequestDataSerialComm(args)
            .then((data) => {
                console.info('Bluetooth data was successfully retrieved')
                requestDataSerialCommFaultMetrics.count(0)
                return data
            })

        return PromiseUtils.executeWithTimeout(bluetoothRequestDataSerialCommPromise, {
            durationMillis: BluetoothSerialCommTimeout.DEFAULT,
            error: new Error('BluetoothSerialComm requestDataSerialComm() execution timed out.'),
        })
            .catch((error) => {
                console.error('BluetoothSerialComm requestDataSerialComm failure with error: ', error)
                requestDataSerialCommFaultMetrics.count(1)
                return Promise.reject(CandyCaneBluetoothErrorMappings.getExceptionForError(error))
            })
            .finally(() => {
                requestDataSerialCommLatencyMetrics.stop()
            })
    }
}
